import * as angular from 'angular';
import { FeatureTogglesResource } from 'angular/resources/feature-toggles.resource';
import { getCognitoToken } from 'angular/utils/cognito';
import {
    config as stageConfig,
    embedded,
    reactShell
} from 'angular/utils/config';
import { Decimal } from 'decimal.js';
import * as moment from 'pkp-moment-timezone-all-years';
import { addBreadcrumb } from '../../../../../src/utils/breadcrumbs';
import { CapacityManagementService } from './_services/capacityManagementService';
import { OrganisationService } from './_services/organisationService';
import { FullscreenToogleService } from 'app/_services/fullscreenToggleService';
import { PrivilegeService } from 'app/_services/privilegeService';

function config(
    $provide: ng.auto.IProvideService,
    $logProvider: ng.ILogProvider,
    $httpProvider: ng.IHttpProvider,
    $translateProvider: ng.translate.ITranslateProvider,
    ngIntlTelInputProvider
) {
    ngIntlTelInputProvider.set({
        separateDialCode: false,
        nationalMode: false,
        utilsScript: 'build/js/utils.js',
        preferredCountries: [
            'AT',
            'BE',
            'BG',
            'CH',
            'CY',
            'CZ',
            'DE',
            'DK',
            'ES',
            'FR',
            'GB',
            'GR',
            'HR',
            'HU',
            'IE',
            'IT',
            'LU',
            'MT',
            'NL',
            'NO',
            'PL',
            'PT',
            'RO',
            'RS',
            'RU',
            'SE',
            'SI',
            'SK',
            'UA'
        ],
        autoPlaceholder: 'polite',
        placeholderNumberType: 'FIXED_LINE',
        initialCountry: 'auto'
    });
    Decimal.set({
        defaults: true,
        toExpNeg: -9,
        toExpPos: 19
    });

    /**
     * This adds a boolean flag `loading` to the `$rootScope` that is `true`
     * if there's currently any promise being deferred (an XHR request in progress).
     */
    function provideLoadingStateInRootScope(
        $delegate: any,
        $rootScope: ng.IRootScopeService
    ): ng.IQService {
        var pendingPromises = 0;

        $rootScope.$watch(
            () => pendingPromises > 0,
            (loading) => (($rootScope as any).loading = loading)
        );

        var $q: ng.IQService = $delegate;
        var origDefer = $q.defer;

        $q.defer = <T>(): ng.IDeferred<T> => {
            const defer = origDefer<T>();
            pendingPromises++;
            defer.promise.finally(() => pendingPromises--);
            return defer;
        };

        return $q;
    }

    function extendAngularWizardDirective($delegate: any) {
        let directive: ng.IDirective = $delegate[0];

        // Set default template.
        directive.templateUrl = (elem, attrs) =>
            attrs['template'] || 'app/includes/wizard-template.html';

        // Extend directive with an addition inputSummary scope param.
        angular.extend(directive.scope, {
            inputSummary: '@',
            hideHeading: '='
        });

        return $delegate;
    }

    function extendUibDatepickerPopup($delegate: any) {
        const directive: ng.IDirective = $delegate[0];
        const scopeExtension = {
            timeModel: '=',
            timeDisabled: '=',
            dateDisabled: '=',
            hideCustomButtomBar: '=',
            onTimePickerValueChange: '&'
        };

        angular.extend(directive.scope, scopeExtension);
        return $delegate;
    }

    $provide.decorator('$q', [
        '$delegate',
        '$rootScope',
        provideLoadingStateInRootScope
    ]);
    $provide.decorator('wizardDirective', extendAngularWizardDirective);
    $provide.decorator('uibDatepickerPopupDirective', extendUibDatepickerPopup);

    /*
     * Set up the translation module and the log provider.
     */
    $translateProvider.useSanitizeValueStrategy('sanitize');
    $translateProvider.useStaticFilesLoader({
        files: [
            {
                prefix: process.env.PUBLIC_PATH
                    ? process.env.PUBLIC_PATH + 'app/locale/locale-'
                    : '/angular/app/locale/locale-',
                suffix: '.json'
            }
        ]
    });
    $translateProvider.preferredLanguage('en');
    $logProvider.debugEnabled(true);

    /*
     * responseError: If response error returned with 401, the user will be logged out.
     * response: Convert strings to decimal values according to dedicated flag.
     *  See {@link bigDecimalConverterService}.
     */
    $httpProvider.interceptors.push(function (
        $q: ng.IQService,
        privilegeService: PrivilegeService,
        authService,
        bigDecimalConverterService,
        $injector: ng.auto.IInjectorService
    ) {
        return {
            request: function (config) {
                const isMonolithRequest = config.url.startsWith(
                    stageConfig.monolithApi
                );

                if (!isMonolithRequest) return config;

                if (config.method === 'GET') {
                    // No caching, please. This is primarily an IE issue.
                    config.params = config.params || {};
                    config.params._ = new Date().getTime();
                }

                // role header
                if (localStorage.getItem('ENFORCE_SHIPPER_ROLE') === 'true')
                    config.headers['X-Prisma-Switch-User'] = 'true';

                // set token
                config.headers = config.headers || {};
                if (config.headers.Authorization) return config;

                return getCognitoToken().then((token) => {
                    if (token) config.headers.Authorization = `Bearer ${token}`;
                    return config;
                });
            },
            responseError: function (rejection) {
                const $http = $injector.get('$http');
                const WithRefreshedTokenHeader =
                    'X-Angular-Interceptor-Jwt-Refresh';
                if (
                    rejection.status ===
                        PrivilegeService.STATUS_CODE.UNAUTHORIZED &&
                    rejection.config
                ) {
                    const config: ng.IRequestConfig = rejection.config;
                    const headers = config.headers || {};
                    return getCognitoToken().then((token) => {
                        if (
                            headers[WithRefreshedTokenHeader] === 'true' ||
                            token === null
                        ) {
                            authService.logOut();
                        }

                        if (
                            headers[WithRefreshedTokenHeader] !== 'true' &&
                            token
                        ) {
                            // if possible, retry request with new token
                            return $http<any>({
                                ...config,
                                headers: {
                                    ...config.headers,
                                    [WithRefreshedTokenHeader]: 'true',
                                    Authorization: `Bearer ${token}`
                                }
                            });
                        }

                        return $q.reject(rejection);
                    });
                }

                return $q.reject(rejection);
            },
            response: function (response) {
                addBreadcrumb({
                    type: 'http',
                    response: {
                        method: response.config.method,
                        url: response.config.url,
                        status: response.status,
                        statusText: response.statusText
                    },
                    frontend: 'angular'
                });

                if (!angular.isObject(response.data)) {
                    return response;
                }

                var clonedResponse = angular.copy(response);
                clonedResponse.data = bigDecimalConverterService.convert(
                    clonedResponse.data
                );
                return clonedResponse;
            }
        };
    });
}

function run(
    $rootScope: ng.IRootScopeService,
    $location: ng.ILocationService,
    $route: ng.route.IRouteParamsService,
    privilegeService: PrivilegeService,
    authService,
    reportingStorageService,
    activeLocationService,
    amMoment,
    fullscreenToogleService: FullscreenToogleService,
    featureTogglesResource: FeatureTogglesResource,
    camaService: CapacityManagementService,
    organisationService: OrganisationService,
    cfpLoadingBar
) {
    // make $location globally available, so it can be used by React (and some Angular code)
    (window as any).__angularLocation = $location;

    ($rootScope as any).withinReactShell = embedded;

    if (embedded) {
        let lastKnownValidUrl = '';

        $rootScope.$on(
            '$locationChangeStart',
            function (event, newUrl, oldUrl, newState, oldState) {
                // I don't know why this only happens in production and never locally,
                // but in production every correct hash change get's immediately resetted by an
                // empty hash (case: newUrl === stageConfig.appBaseUrl + '/angular') which is
                // followed by a redirect to the "otherwise" route, because the empty hash is not valid
                // (case: oldUrl === stageConfig.appBaseUrl + '/angular').
                //
                // In general there *should* never be an empty hash, so we should be safe to ignore them.
                //
                // (Why should there be no empty hash? Well, we initialize the hash with '#/initialize'
                // and afterwards it should be at least '#/' or something with a concrete path, if no one
                // manipulates the URL with some custom logic.)
                if (
                    newUrl === stageConfig.appBaseUrl + '/angular' ||
                    oldUrl === stageConfig.appBaseUrl + '/angular'
                ) {
                    event.preventDefault();
                    $location
                        .path(new URL(lastKnownValidUrl).hash.replace('#/', ''))
                        .replace();
                    return;
                }

                lastKnownValidUrl = newUrl;

                // e.g. '/MR-1143/angular/'.split('/') -> ['', 'MR-1143', 'angular', '']
                const splittedPathname =
                    window.parent.location.pathname.split('/');
                const firstPathSegment = splittedPathname.splice(1, 1)[0];
                const isMR = Boolean(firstPathSegment.match(/^MR-[\d]*$/));
                const pathname = isMR
                    ? splittedPathname.join('/')
                    : window.parent.location.pathname;

                // regex pathname ends with "/platform" or "/platform/"
                if (!pathname.match(/\/platform\/?$/)) return;

                // e.g. https://app.ent.prisma-capacity.cloud/MR-1143/angular/#/start -> #/start
                // using decodeURIComponent to handle special characters like ä, ö, ü, ß and cyrillic.
                // without it, we entering the loop of auth requests =>> https://prisma.atlassian.net/browse/UXX-361
                const hash = decodeURIComponent(
                    newUrl
                        // replace base url
                        .replace(stageConfig.appBaseUrl + '/angular', '')
                        // replace leading '/' (it seems to only be prepended in production)
                        .replace(/^\//, '')
                );

                if (hash === '#/initialize') return;
                if (hash === '#/loading') return;

                if (window.parent.location.hash === hash) {
                    // parent is synced with angular
                } else {
                    // parent needs to be synced with angular
                    window.parent.location.hash = hash;
                }
            }
        );

        (window as any).__ANGULAR_APP_LOADED__ = true;

        // handle loading bar animation
        let loadingBar;
        const fadeOutMs = 650;
        let animationTimeout;
        $rootScope.$watch(
            () => cfpLoadingBar.status(),
            (newVal, oldVal) => {
                if (!loadingBar)
                    loadingBar =
                        window.parent.document.getElementById(
                            'react-loading-bar'
                        );
                // just in case the iframe finishes way earlier then the react app mounts
                if (!loadingBar) return;

                if (oldVal === 1) return; // we handle the reset in the timeout
                if (animationTimeout) clearTimeout(animationTimeout); // interrupt the timeout

                loadingBar.style.transition = `350ms linear width, ${fadeOutMs}ms linear opacity`;
                loadingBar.style.width = `${newVal * 100}%`;

                if (newVal === 1) {
                    loadingBar.style.opacity = 0;

                    animationTimeout = setTimeout(() => {
                        loadingBar.style.transition = '';
                        loadingBar.style.width = '0%';
                        loadingBar.style.opacity = 1;
                    }, fadeOutMs);
                }
            }
        );
    }

    ($rootScope as any).reactShell = reactShell;
    ($rootScope as any).stageConfig = stageConfig;
    ($rootScope as any).currentYear = new Date().getFullYear();

    ($rootScope as any).camaService = camaService;
    ($rootScope as any).organisationService = organisationService;

    featureTogglesResource
        .getUrls()
        .subscribe(
            (featureToggles) =>
                (($rootScope as any).featureToggles = featureToggles)
        );

    amMoment.changeLocale('en');
    moment.tz.setDefault('Europe/Berlin');

    /**
     * If a route was changed with a 401 UNAUTHORIZED HTTP status code returned,
     * the access denied page will be shown.
     */
    function onRouteChangeError(event, current, previous, rejection) {
        if (rejection === PrivilegeService.STATUS_CODE.UNAUTHORIZED) {
            $location.path('/access-denied');
        }
        if (rejection === PrivilegeService.STATUS_CODE.FORBIDDEN) {
            $location.path('/permission-denied');
        }
        $.notifyClose();
    }

    function onLocationChangeSuccess() {
        fullscreenToogleService.checkAutoToggle();
        $.notifyClose();
    }

    function onWizardStepChanged() {
        $.notifyClose();
    }

    $rootScope.$on('$routeChangeError', onRouteChangeError);
    $rootScope.$on('$locationChangeSuccess', onLocationChangeSuccess);
    $rootScope.$on('wizard:stepChanged', onWizardStepChanged);
}

angular.module('app').config(config).run(run);
